import React, { useContext, useEffect } from "react";
import { useRef, useState } from "react";

import { useMutation, useQuery } from "@tanstack/react-query";
import { Button, Col, Divider, Form, Grid, Input, Popconfirm, Row, Select, Skeleton, Switch, notification } from "antd";
import { useForm, useWatch } from "antd/es/form/Form";

import AdicionarAjuda, { ITutorial, ITutorialPassos } from "../../../Components/AdicionarAjuda";
import { AgenciaService } from "../../../Services/Financeiro/AgenciaService";
import { ContaService } from "../../../Services/Financeiro/ContaService";
import { BancoService } from "../../../Services/Financeiro/BancoService";
import { EmpresaService } from "../../../Services/Pessoa/EmpresaService";
import { PropriedadesDoPopConfirm } from "../../../Defaults";
import { PropriedadesDeNotificacao } from "../../../Utils/Notificacoes";
import { SelectComPesquisa } from "../../../Components/SelectComPesquisa";
import { ContaProvedorConciliacaoLista, EnumTipoConta, TypeConta } from "../../../Models/Financeiro/Conta";

import { Permissoes } from "../../../Constants/Permissoes";
import { SessaoContext } from "../../../Contexts/SessaoContext";
import { Restrito } from "../../../Components/Restrito";

import { ResponsiveAntdTable } from "../../../Components/ResponsiveAntdTable";
import { Conjunto } from "../../../Components/Conjunto";
import { Ordenar } from "../../../Utils/Ordenacao";

import { Link, matchPath, Route, Routes, useNavigate } from "react-router-dom";
import { useRelativePath } from "../../../Hooks/useNextRelativePath";
import { useRedirection } from "../../../Hooks/useRedirection";
import { useWatchParameterOnRoutes } from "../../../Hooks/useGetParameterOnRoutes";

import * as Tutoriais from "./conta.tutorial";
import ConfiguracaoFormBasic from "../../../Components/Populados/Financeiro/Conta/Remessa/Configuracao/ConfiguracaoFormBasic";
import PlanoDeContasSelect from "../../../Components/Populados/Financeiro/PlanoDeContas/PlanoDeContasSelect";
import { EnumPlanoContasTipoMovimentacao } from "../../../Models/Financeiro/PlanoContasTipo";
import { EnumRegistroTipo } from "../../../Models/Financeiro/PlanoContasTipoRegistro";
import CentroCustoSelect from "@/Components/Populados/Financeiro/CentroConta/CentroCustoSelect";
import { useCentroCustoListar } from "@/Services/Financeiro/CentroCustoService";
import { EnumCentroCustoTipo } from "@/Models/Financeiro/CentroCustoTipo";
import { TypeCentroCusto } from "@/Models/Financeiro/CentroCusto";

const agenciaService = new AgenciaService();
const contaService = new ContaService();
const bancoService = new BancoService();
const empresaService = new EmpresaService();

const ListagemDeContas: React.FC<{
    aoEditar?: (item: TypeConta) => any,
    aoAdicionar?: () => any

    hrefDoBotaoAdicionar?: string,
    hrefDoBotaoEditar?: (conta: TypeConta) => string | void
}> = (props) => {

    const permissoes = useContext(SessaoContext)?.sessao?.usuario?.permissoes.map((p) => p.nome);
    const tamanhoDaTela = Grid.useBreakpoint();

    const [filtro, setFiltro] = useState<TypeConta>({} as TypeConta);

    const listaConta = useQuery({
        queryKey: ['listarContas', filtro],
        queryFn: () => contaService.listar(filtro, undefined, undefined, [EnumTipoConta.ADIANTAMENTO_VIAGEM]),
        gcTime: 0,
        enabled: true
    });

    interface IFormFiltro {
        txtNomeConta?: string,
        txtNumeroConta?: string,
        ddlAtivo?: boolean
    }

    const filtrarTabela = (values: IFormFiltro) => {
        const contaFiltro: TypeConta = {
            nome: values.txtNomeConta,
            numeroConta: values.txtNumeroConta,
            ativo: values.ddlAtivo,
        } as TypeConta;
        setFiltro(contaFiltro);
    }

    const adicionar = permissoes?.includes(Permissoes.CriarConta);

    return (
        <>
            <Form name="listar" onFinish={filtrarTabela} layout='vertical' autoComplete="off">
                <Row gutter={16} id={Tutoriais.IDs.formularioDeFiltro} >
                    <Col xs={24} md={6} id={Tutoriais.IDs.filtroDeNomeConta}>
                        <Form.Item<IFormFiltro> name="txtNomeConta" label='Nome da conta'>
                            <Input placeholder="Todos"/>
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={7} id={Tutoriais.IDs.filtroDeNumeroConta}>
                        <Form.Item<IFormFiltro> name="txtNumeroConta" label='Número da conta'>
                            <Input placeholder="Todos"/>
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={3} id={Tutoriais.IDs.filtroDeAtivo}>
                        <Form.Item<IFormFiltro> name="ddlAtivo" label='Ativo' initialValue={true}>
                            <Select
                                allowClear
                                placeholder={"Todos"}
                                options={[
                                    { value: true, label: "Ativo" },
                                    { value: false, label: "Inativo" }
                                ]} />
                        </Form.Item>
                    </Col>
                    <Col xs={adicionar ? 12 : 24} md={adicionar ? 3 : 5} style={{ alignItems: 'end', display: 'flex' }}>
                        <Form.Item style={{ flexGrow: 1 }}>
                            <Button type="primary" id={Tutoriais.IDs.botaoListar} key="submit" htmlType='submit' style={{ width: "100%" }}
                                disabled={listaConta.isFetching}
                            >
                                Listar
                            </Button>
                        </Form.Item>
                    </Col>
                    
                    <Restrito aberto={adicionar}>
                        <Col xs={12} md={5} style={{ alignItems: 'end', display: 'flex' }}>
                            <Form.Item style={{ flexGrow: 1 }}>
                                <Link to={props.hrefDoBotaoAdicionar ?? "#"} onClick={(e) => { !props.hrefDoBotaoAdicionar && e.preventDefault() }}>
                                    <Button type="primary" onClick={() => { props.aoAdicionar?.() }} style={{ width: "100%" }}>
                                        Adicionar
                                    </Button>
                                </Link>
                            </Form.Item>
                        </Col>
                    </Restrito>
                </Row>
                <Row>
                    <Col xs={24}>
                        <ResponsiveAntdTable<TypeConta>
                            bordered
                            locale={{ emptyText: 'Nenhum conta encontrado.' }}
                            dataSource={listaConta.data?.data}
                            loading={listaConta.isFetching}
                            rowKey={(k) => k.codigo!}
                            addTitleOn="lg"
                            columns={[
                                {
                                    title: "Nome",
                                    key: "conta-nome",
                                    breakpoints: { xs: 24, md: 12, lg: 6 },
                                    dataIndex: ["nome"],
                                    sorter: (a, b) => Ordenar.String(a.nome, b.nome),
                                },
                                {
                                    title: "Agência",
                                    key: "conta-nome-agencia",
                                    breakpoints: { xs: 24, md: 12, lg: 6 },
                                    dataIndex: ["agencia", "nomeAgencia"],
                                    sorter: (a, b) => Ordenar.String(a.agencia?.nomeAgencia, b.agencia?.nomeAgencia),
                                },
                                {
                                    title: "Conta",
                                    key: "conta-numero",
                                    breakpoints: { xs: 24, md: 8, lg: 3 },
                                    dataIndex: ["numeroConta"],
                                    sorter: (a, b) => Ordenar.Numero(a.numeroConta != undefined ? parseInt(a.numeroConta) : undefined, b.numeroConta != undefined ? parseInt(b.numeroConta) : undefined),
                                },
                                {
                                    title: "Dígito",
                                    key: "conta-digito",
                                    breakpoints: { xs: 24, md: 8, lg: 3 },
                                    dataIndex: ["digitoConta"],
                                    sorter: (a, b) => Ordenar.Numero(a.digitoConta != undefined ? parseInt(a.digitoConta) : undefined, b.digitoConta != undefined ? parseInt(b.digitoConta) : undefined),
                                },
                                {
                                    title: "Ativo",
                                    key: "conta-ativo",
                                    dataIndex: ["ativo"],
                                    breakpoints: { xs: 24, md: 8, lg: 2 },
                                    sorter: (a, b) => Ordenar.Booleano(a.ativo, b.ativo),
                                    render: (v: boolean) => {
                                        return <>{v ? "Sim" : "Não"}</>
                                    }
                                },
                                {
                                    title: "Ações",
                                    key: "conta-acoes",
                                    noResponsiveTitle: true,
                                    breakpoints: { xs: 24, md: 24, lg: 4 },
                                    hidden: [Permissoes.AtualizarConta, Permissoes.DesativarConta, Permissoes.AtivarConta].every((p) => !permissoes?.includes(p)),
                                    render: (_, item) => {
                                        return <Conjunto
                                            style={{ justifyContent: tamanhoDaTela.sm ? "unset" : "center", display: "flex", flexWrap: 'wrap' }}
                                            itens={
                                                (() => {
                                                    if (item.ativo) {
                                                        return [
                                                            permissoes?.includes(Permissoes.CriarConta) &&
                                                            <Link to={props.hrefDoBotaoEditar?.(item) ?? "#"}
                                                                onClick={(e) => !props.hrefDoBotaoEditar && e.preventDefault()}
                                                            >
                                                                <Button
                                                                    className={Tutoriais.Classes.botaoEditar}
                                                                    type='primary'
                                                                    size='small'
                                                                    onClick={() => {
                                                                        props.aoEditar?.(item);
                                                                    }}
                                                                >
                                                                    Editar
                                                                </Button>
                                                            </Link>,

                                                            permissoes?.includes(Permissoes.DesativarConta) &&
                                                            <Popconfirm
                                                                {...PropriedadesDoPopConfirm("desativar")}
                                                                onConfirm={() => {
                                                                    if (item.codigo == undefined) return;
                                                                    contaService.desativar(item.codigo)
                                                                        .then(() => {
                                                                            listaConta.refetch()
                                                                        })
                                                                        .catch(() => { })
                                                                }}
                                                            >
                                                                <Button type='default' danger size='small' className={Tutoriais.Classes.botaoDesativar}>
                                                                    Desativar
                                                                </Button>
                                                            </Popconfirm>
                                                        ]
                                                    } else {
                                                        return [
                                                            permissoes?.includes(Permissoes.AtivarConta) &&
                                                            <Popconfirm
                                                                {...PropriedadesDoPopConfirm("ativar")}
                                                                onConfirm={() => {
                                                                    if (item.codigo == undefined) return;
                                                                    contaService.ativar(item.codigo)
                                                                        .then(() => {
                                                                            listaConta.refetch()
                                                                        })
                                                                        .catch(() => { })
                                                                }}
                                                            >
                                                                <Button type='dashed' size='small'>Reativar</Button>
                                                            </Popconfirm>
                                                        ]
                                                    }

                                                })()
                                            }
                                        />
                                    }
                                },
                            ]}

                            csv={{
                                enabled: true,
                                filename: "contas.csv"
                            }}
                        />
                    </Col>
                </Row>

            </Form>

            <AdicionarAjuda tutoriais={[
                {
                    titulo: "Como consultar contas",
                    tutorial: Tutoriais.ConsultarConta
                },

                permissoes?.includes(Permissoes.CriarConta) &&
                {
                    titulo: "Como cadastrar um novo conta",
                    tutorial: Tutoriais.AdicionarConta
                },

                permissoes?.includes(Permissoes.AtualizarConta) &&
                {
                    titulo: "Como editar um conta",
                    tutorial: Tutoriais.EditarConta
                },

                permissoes?.includes(Permissoes.DesativarConta) &&
                {
                    titulo: "Como desativar um conta",
                    tutorial: Tutoriais.DesativarConta
                }
            ].filter((v) => v != false) as ITutorial[]} />
        </>
    )
}

const CadastroDeConta: React.FC<{
    contaEmAlteracao?: TypeConta,
    aoCancelar?: () => any,
    depoisDeFinalizar?: () => any
}> = (props) => {

    const usuario = useContext(SessaoContext)?.sessao?.usuario;

    const insereConta = useMutation({
        mutationFn: (conta: TypeConta) => contaService.inserir(conta),
        onSuccess: () => {
            notification.open({
                ...PropriedadesDeNotificacao.Sucesso('Cadastro realizado com sucesso.')
            })
            props.depoisDeFinalizar && props.depoisDeFinalizar()
        },
    });

    const atualizaConta = useMutation({
        mutationFn: (conta: TypeConta) => contaService.atualizar(conta),
        onSuccess: () => { 
            notification.open({
                ...PropriedadesDeNotificacao.Sucesso('Atualização realizada com sucesso.')
            })
            props.depoisDeFinalizar && props.depoisDeFinalizar()
        },
    });

    function onFinishConta(values: TypeConta) {
        values.remessaConfiguracao = !!values.remessaConfiguracao && !values.remessaConfiguracao.ativo ? null : values.remessaConfiguracao;

        const contaInserir: TypeConta = {
            ...values,
            // codCentroCustoTarifa : values.centroCustoTarifa?.codigo ?? null,
            // codPlanoContaTarifa : values.planoContaTarifa?.codigo ?? null,
            codPlanoConta_TaxasGetNet : values.planoConta_TaxasGetNet?.codigo ?? null,
            codPlanoContasTarifaReembolso : values.planoContasTarifaReembolso?.codigo ?? null,
            codPlanoConta_TaxasBoleto : values.planoConta_TaxasBoleto?.codigo ?? null,
            codPlanoContasTarifaBoletoReembolso : values.planoContasTarifaBoletoReembolso?.codigo ?? null,
            codigo: props.contaEmAlteracao ? props.contaEmAlteracao.codigo : undefined
        }

        if (contaInserir?.codigo === undefined)
            insereConta.mutate(contaInserir!);
        else
            atualizaConta.mutate(contaInserir!);
    }

    const [formCadastro] = useForm<TypeConta>();
    const codEmpresaWatch = useWatch(["codEmpresa"], formCadastro);
    const [ geraRemessa, setGeraRemessa ] = useState(false);
    const geraRemessaOnline = Form.useWatch(["remessaConfiguracao", "ativo"], formCadastro);

    useEffect(() => {
        if(props.contaEmAlteracao) formCadastro.setFieldsValue(props.contaEmAlteracao);
    }, [props.contaEmAlteracao])

    const listaAgencias = useQuery({
        queryKey: ['listarAgencias'],
        queryFn: () => agenciaService.listar({ ativo: true }),
        gcTime: 0,
        enabled: true
    });

    const listaEmpresas = useQuery({
        queryKey: ['listarEmpresas'],
        queryFn: () => empresaService.listar({ ativo: true }),
    });

    const ddlEmpresa = useRef(null);
    const ddlTipo = useRef(null);
    const ddlAgencia = useRef(null);
    const txtNome = useRef(null);
    const txtNumero = useRef(null);
    const txtDigito = useRef(null);
    const ddlAtivo = useRef(null);
    const btnSalvar = useRef(null);

    const tutorialAdicionarConta: ITutorialPassos = {
        steps: [
            {
                title: "Passo 1",
                description: "Selecione a empresa.",
                target: () => ddlEmpresa.current,
            },
            {
                title: "Passo 2",
                description: "Selecione o tipo da conta.",
                target: () => ddlTipo.current,
            },
            {
                title: "Passo 3",
                description: "Selecione a agência.",
                target: () => ddlAgencia.current,
            },
            {
                title: "Passo 4",
                description: "(Opcional) Informe um nome para a conta.",
                target: () => txtNome.current,
            },
            {
                title: "Passo 5",
                description: "(Opcional) Informe o número da conta.",
                target: () => txtNumero.current,
            },
            {
                title: "Passo 6",
                description: "(Opcional) Informe o dígito da conta neste campo, caso haja.",
                target: () => txtDigito.current,
            },
            {
                title: "Passo 7",
                description: `Por fim, clique em "Salvar".`,
                target: () => btnSalvar.current,
            }
        ]
    }
    const tutorialMenuItems: ITutorial[] = [
        {
            titulo: "Como adicionar uma nova conta",
            tutorial: tutorialAdicionarConta
        },
    ]

    const centrosDeCustos = useCentroCustoListar({
        centroCusto: {
            tipo: {
                codigo: EnumCentroCustoTipo.ITEM,
            },
            empresa: {
                codigo: codEmpresaWatch === undefined || codEmpresaWatch <= 0 ? undefined : (codEmpresaWatch as number | undefined),
            },
        },
    });

    

    return (
        <>
            <Form autoComplete="off" onFinish={onFinishConta} form={formCadastro} layout="vertical">
                <Row gutter={[8, 8]}>
                    <Col xs={24} sm={6} lg={6} ref={txtNome}>
                        <Form.Item<TypeConta> name="nome" label="Nome" rules={[{ required : true }]}>
                            <Input placeholder="Nome" />
                        </Form.Item>
                    </Col>

                    <Col xs={24} sm={6} lg={6} ref={ddlEmpresa}>
                        <Form.Item<TypeConta>
                            name="codEmpresa"
                            label="Empresa"
                            rules={[
                                {
                                    required: true,
                                    message: "Por favor, selecione a empresa!",
                                },
                            ]}
                        >
                            <SelectComPesquisa
                                allowClear
                                placeholder={"Selecione a empresa"}
                                options={listaEmpresas.data?.data.map((m) => {
                                    return {
                                        value: m.codigo,
                                        label: m.nome!,
                                    };
                                })}
                            />
                        </Form.Item>
                    </Col>

                    <Col xs={24} sm={6} lg={6} ref={ddlTipo}>
                        <Form.Item<TypeConta>
                            name="codTipoConta"
                            label="Tipo"
                            rules={[
                                {
                                    required: true,
                                    message: "Por favor, selecione o tipo da conta!",
                                },
                            ]}
                        >
                            <Select
                                allowClear
                                placeholder={"Selecione o tipo da conta"}
                                options={[
                                    { value: EnumTipoConta.CONTACORRENTE, label: "Conta Corrente" },
                                    { value: EnumTipoConta.INVESTIMENTO, label: "Investimento" },
                                    { value: EnumTipoConta.CAIXA, label: "Caixa" },
                                    { value: EnumTipoConta.ADQUIRENTE_CARTEIRA_DIGITAL, label: "Adquirente/Carteira Digital" },
                                    // { value: EnumTipoConta.ADIANTAMENTO_VIAGEM, label: "Adiantamento de Despesas de Viagem" }
                                ]}
                            />
                        </Form.Item>
                    </Col>

                    <Col xs={24} sm={6} lg={6} ref={ddlAgencia}>
                        <Form.Item<TypeConta>
                            name="codAgencia"
                            label="Agência"
                            rules={[
                                {
                                    required: true,
                                    message: "Por favor, selecione a agência!",
                                },
                            ]}
                        >
                            <SelectComPesquisa
                                allowClear
                                placeholder={"Selecione a agência"}
                                options={listaAgencias.data?.data.map((m) => {
                                    return {
                                        value: m.codigo,
                                        label: m.nomeAgencia!,
                                    };
                                })}
                            />
                        </Form.Item>
                    </Col>

                    <Col xs={24} sm={8} lg={4} ref={txtNumero}>
                        <Form.Item<TypeConta>
                            name="numeroConta"
                            // rules={[
                            //     {
                            //         validator: async (_, val: string) => {
                            //             if (val == undefined || val == null)
                            //                 return;

                            //             if (!val.match(/^[0-9]*$/))
                            //                 return Promise.reject("Valor inválido.");
                            //         }
                            //     }
                            // ]}
                            label="Número da conta"
                        >
                            <Input placeholder="Número" />
                        </Form.Item>
                    </Col>

                    <Col xs={24} sm={8} lg={3} ref={txtDigito}>
                        <Form.Item<TypeConta>
                            name="digitoConta"
                            // rules={[
                            //     {
                            //         validator: async (_, val: string) => {
                            //             if (val == undefined)
                            //                 return

                            //             if (!val.match(/^[0-9]*$/))
                            //                 return Promise.reject("Valor inválido.")
                            //         }
                            //     }
                            // ]}
                            label="Dígito"
                        >
                            <Input placeholder="Digito" />
                        </Form.Item>
                    </Col>

                    <Col xs={24} sm={8} lg={5}>
                        <Form.Item<TypeConta> name="codProvedorConciliacao" label="Provedor de conciliação">
                            <SelectComPesquisa
                                allowClear
                                placeholder={"Selecione um Provedor"}
                                options={ContaProvedorConciliacaoLista}
                            />
                        </Form.Item>
                    </Col>

                    <Col xs={24} sm={8} lg={5}>
                        <Form.Item<TypeConta> name="idConciliacao" label="ID de conciliação">
                            <Input placeholder="ID de conciliação" />
                        </Form.Item>
                    </Col>

                    {/* <Col xs={24} sm={12} lg={6}>
                        <Form.Item<TypeConta>
                            label="P.C. (tarifas)"
                            name={["planoContaTarifa"]}
                            rules={[
                                {
                                    required: true,
                                },
                            ]}
                        >
                            <PlanoDeContasSelect
                                filtros={{
                                    tipo: {
                                        codigo: EnumPlanoContasTipoMovimentacao.Despesa,
                                    },
                                    tipoRegistro: {
                                        codigo: EnumRegistroTipo.Item,
                                    },
                                    empresa: {
                                        codigo: codEmpresaWatch === undefined || codEmpresaWatch <= 0 ? undefined : (codEmpresaWatch as number | undefined),
                                    },
                                }}
                            />
                        </Form.Item>
                    </Col> */}

                    {/*<Col xs={24} sm={12} lg={6}>
                        <Form.Item<TypeConta>
                            label="Centro de custos (tarifas)"
                            name={["codCentroCustoTarifa"]}
                            rules={[
                                {
                                    required: false,
                                },
                            ]}
                            initialValue={3}
                        >
                            <Select options={centrosDeCustos.data?.map((c: TypeCentroCusto) => ({value: c.codigo, label: c.descricao}))}/>
                        </Form.Item>
                    </Col>*/}

                    <Col xs={24} sm={12} lg={6}>
                        <Form.Item<TypeConta>
                            label="P.C. Taxa (Geral)"
                            name={["planoConta_TaxasGetNet"]}
                            rules={[
                                {
                                    required: false,
                                },
                            ]}
                        >
                            <PlanoDeContasSelect
                                filtros={{
                                    tipo: {
                                        codigo: EnumPlanoContasTipoMovimentacao.Despesa,
                                    },
                                    tipoRegistro: {
                                        codigo: EnumRegistroTipo.Item,
                                    },
                                    empresa: {
                                        codigo: codEmpresaWatch === undefined || codEmpresaWatch <= 0 ? undefined : (codEmpresaWatch as number | undefined),
                                    },
                                }}
                            />
                        </Form.Item>
                    </Col>

                    

                    <Col xs={24} sm={12} lg={6}>
                        <Form.Item<TypeConta>
                            label="P.C. Taxa Reembolso (Geral)"
                            name={["planoContasTarifaReembolso"]}
                            rules={[
                                {
                                    required: false,
                                },
                            ]}
                        >
                            <PlanoDeContasSelect
                                filtros={{
                                    tipo: {
                                        codigo: EnumPlanoContasTipoMovimentacao.Receita,
                                    },
                                    tipoRegistro: {
                                        codigo: EnumRegistroTipo.Item,
                                    },
                                    empresa: {
                                        codigo: codEmpresaWatch === undefined || codEmpresaWatch <= 0 ? undefined : (codEmpresaWatch as number | undefined),
                                    },
                                }}
                            />
                        </Form.Item>
                    </Col>

                    {props.contaEmAlteracao && (
                        <Col xs={24} md={6} lg={6} ref={ddlAtivo}>
                            <Form.Item name="ativo" label="Ativo">
                                <Select
                                    allowClear
                                    placeholder={"Selecione"}
                                    options={[
                                        { value: true, label: "Sim" },
                                        { value: false, label: "Não" },
                                    ]}
                                />
                            </Form.Item>
                        </Col>
                    )}
                </Row>

                <Row gutter={[8, 8]}>
                    <Col xs={"auto"}>
                        <Form.Item<TypeConta> name={["remessaConfiguracao", "ativo"]} label="Gera remessa?">
                            <Switch checkedChildren="Sim" unCheckedChildren="Não" />
                        </Form.Item>
                    </Col>
                    
                    {geraRemessaOnline && <Col xs={24} sm={12} lg={6}>
                        <Form.Item<TypeConta>
                            label="P.C. Taxa (Boleto)"
                            name={["planoConta_TaxasBoleto"]}
                            rules={[
                                {
                                    required: true,
                                },
                            ]}
                        >
                            <PlanoDeContasSelect
                                filtros={{
                                    tipo: {
                                        codigo: EnumPlanoContasTipoMovimentacao.Despesa,
                                    },
                                    tipoRegistro: {
                                        codigo: EnumRegistroTipo.Item,
                                    },
                                    empresa: {
                                        codigo: codEmpresaWatch === undefined || codEmpresaWatch <= 0 ? undefined : (codEmpresaWatch as number | undefined),
                                    },
                                }}
                            />
                        </Form.Item>
                    </Col>}

                    {geraRemessaOnline && <Col xs={24} sm={12} lg={6}>
                        <Form.Item<TypeConta>
                            label="P.C. Reembolso Taxa (Boleto)"
                            name={["planoContasTarifaBoletoReembolso"]}
                            rules={[
                                {
                                    required: true,
                                },
                            ]}
                        >
                            <PlanoDeContasSelect
                                filtros={{
                                    tipo: {
                                        codigo: EnumPlanoContasTipoMovimentacao.Receita,
                                    },
                                    tipoRegistro: {
                                        codigo: EnumRegistroTipo.Item,
                                    },
                                    empresa: {
                                        codigo: codEmpresaWatch === undefined || codEmpresaWatch <= 0 ? undefined : (codEmpresaWatch as number | undefined),
                                    },
                                }}
                            />
                        </Form.Item>
                    </Col>}

                    {geraRemessaOnline && <>
                        <Divider orientation="left">Configuração de remessa</Divider>
                        <ConfiguracaoFormBasic<TypeConta> name={"remessaConfiguracao"} />
                    </>}

                    <Divider />

                    <Row style={{ display: "flex", width: "100%", justifyContent: "center" }}>
                        <Form.Item>
                            {props.aoCancelar && (
                                <Button
                                    danger
                                    type="primary"
                                    onClick={() => {
                                        props.aoCancelar && props.aoCancelar();
                                    }}
                                >
                                    Cancelar
                                </Button>
                            )}
                            <Button
                                type="primary"
                                ref={btnSalvar}
                                key="submit"
                                htmlType="submit"
                                style={{ margin: "0px 8px" }}
                                disabled={
                                    !usuario?.permissoes
                                        .map((p) => p.nome)
                                        .includes(props.contaEmAlteracao != undefined ? Permissoes.AtualizarConta : Permissoes.CriarBanco)
                                }
                            >
                                Salvar
                            </Button>
                        </Form.Item>
                    </Row>
                </Row>
            </Form>
            <AdicionarAjuda tutoriais={tutorialMenuItems} />
        </>
    );
}

export function Conta() {
    const [contaEmAlteracao, setContaEmAlteracao] = useState<TypeConta | undefined>(undefined);
    const permissoes = useContext(SessaoContext)?.sessao?.usuario?.permissoes.map((p) => p.nome);

    const navigate = useNavigate();
    const [raiz, subrota] = useRelativePath();

    const rotaDeEdicao = matchPath({ path: "/editar" }, subrota);
    const rotaDeVisualizacao = matchPath({ path: "/detalhes" }, subrota);

    const obterConta = useMutation({
        mutationFn: (id: number) => new ContaService().listar({ codigo: id, ativo: true } as TypeConta),
        onSuccess: (resposta) => {
            const resultados = resposta.data;
            if (resultados.length == 0) {
                notification.open({
                    ...PropriedadesDeNotificacao.Erro("Nenhum registro foi encontrado para o identificador informado")
                })
                navigate(raiz)
            } else {
                setContaEmAlteracao(resposta.data.at(0))
            }
        }
    })

    useRedirection([Permissoes.ListarContas, Permissoes.CriarConta, Permissoes.AtualizarConta, Permissoes.DesativarConta])
    useWatchParameterOnRoutes({ 
        appliablePaths: [rotaDeEdicao, rotaDeVisualizacao], paramToWatch: "id", filters: [(p) => isNaN(Number(p))], 
        onNotAccepted() { navigate(raiz) },
        onExists(param) { obterConta.mutate(Number(param)); }, 
     })

    return ( 
        <>
            <h2 style={{ marginTop: 0 }}> 
                {
                    rotaDeVisualizacao ? "Detalhes do Conta" : rotaDeEdicao? "Edição de Conta": "Cadastro de Conta"
                }
            </h2>

            <Divider />

            <Routes>
                {
                    permissoes?.includes(Permissoes.ListarContas) &&
                    <Route path="*" index element={
                        <ListagemDeContas
                            hrefDoBotaoAdicionar={raiz + "/cadastro"}
                            hrefDoBotaoEditar={(item) => {
                                if (item.codigo) {
                                    return raiz + `/editar?id=${item.codigo}`
                                }
                            }}

                            aoEditar={(conta) => {
                                setContaEmAlteracao(conta);
                            }}
                        />
                    } />
                }

                {
                    permissoes?.includes(Permissoes.CriarConta) &&
                    <Route path="/cadastro" element={
                        <CadastroDeConta
                            aoCancelar={() => { navigate(raiz) }}
                            depoisDeFinalizar={() => { navigate(raiz) }}
                        />
                    } />
                }

                {
                    permissoes?.includes(Permissoes.AtualizarConta) &&
                    <Route path="/editar" element={
                        contaEmAlteracao == undefined ? <Skeleton active /> :
                            <CadastroDeConta
                                contaEmAlteracao={contaEmAlteracao}
                                aoCancelar={() => { navigate(raiz) }}
                                depoisDeFinalizar={() => { navigate(raiz) }}
                            />
                    } />
                }
            </Routes>
        </>
    );
}